import React, { useMemo } from 'react';
import { AccessorKeyColumnDef, createColumnHelper, RowData } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { CODE_95_ENABLED, useViewPreferences } from '../../../../hooks/view-preferences';
import { findExistingReservation, getCalculatedFreeCapacity } from '../../../../utils/training';
import { useUser } from '../../../../hooks/use-user';
import Table from './Table';
import { ActionCell, DateCell, ExpanderCell } from '../TableCells';
import { textFilter } from '../../Filters/TextFilter';
import { compareDates } from '../../../../utils/sort';
import moment from '../../../../moment';
import Tooltip, { infoIcon } from '../../../common/Tooltip';

export type Slot = Partial<CourseDataEntry>;

export type Enrollment = {
	student: {
		firstName: string;
		insertion: string;
		lastName: string;
		comments: null | string;
		id: number;
	};
	company: {
		name: string;
		id: number;
	};
	certificateExpirationDate: string;
	id: number;
};

type TrainingReservation = {
	company: {
		name: string;
		id: number;
	};
	amountReserved: number;
	amountPlanned: number;
	trainingReservationCompanies: {
		company: {
			name: string;
			id: number;
		};
		amountPlanned: number;
		id: number;
	}[];
	id: number;
};

export type CourseDataEntry = {
	id: number;
	name: string;
	freeCapacity: number;
	start: string;
	startDate: string;
	city: string;
	ccvCode: string;
	soobDiscount: number;
	composite: boolean;
	slots: Slot[];
	enrollments: Enrollment[];
	trainingReservations: TrainingReservation[];
};

type Props = {
	courses: CourseDataEntry[];
	onEnrollClick: (row: RowData) => void;
	onReserveClick: (row: RowData) => void;
};

const columnHelper = createColumnHelper<CourseDataEntry>();

const CourseAgendaOverview = ({ courses, onEnrollClick, onReserveClick }: Props) => {
	const { t } = useTranslation();
	const { activeCompany } = useUser();
	const { isPreferenceOptionEnabled } = useViewPreferences();
	const code95Enabled = isPreferenceOptionEnabled(CODE_95_ENABLED);

	const soobContent = () => (
		<span>
			Let op: De SOOB-subsidie is een indicatie. Alleen als aan alle voorwaarden is voldaan kan de
			subsidie worden verstrekt. Voor meer informatie zie{' '}
			<a href={'https://www.stl.nl/soob-subsidie/'} target={'_blank'} rel="noreferrer">
				SOOB-subsidiepunt
			</a>{' '}
			of neem contact met ons op.
		</span>
	);
	const columns = useMemo(() => {
		return [
			columnHelper.display({
				id: 'expander',
				cell: ExpanderCell,
				enableColumnFilter: false,
			}),
			columnHelper.accessor('start', {
				header: t('trainings.date'),
				cell: DateCell,
				sortingFn: (rowA, rowB) =>
					compareDates(moment(rowA.original.start), moment(rowB.original.start)),
				enableColumnFilter: false,
			}),
			...(code95Enabled
				? [
						columnHelper.accessor('ccvCode', {
							header: t('trainings.ccvCode'),
							filterFn: textFilter,
							meta: {
								filterVariant: 'text',
							},
						}),
					]
				: []),
			...(activeCompany.soob
				? [
						columnHelper.accessor('soobDiscount', {
							header: () => (
								<span>
									{t('trainings.soobDiscount')}
									<Tooltip renderPopoverContent={soobContent}>{infoIcon()}</Tooltip>
								</span>
							),
							cell: (cellInfo) => (cellInfo.getValue() ? `€ ${cellInfo.getValue()}` : null),
							enableColumnFilter: false,
						}),
					]
				: []),
			columnHelper.accessor('name', {
				header: t('trainings.training'),
				filterFn: textFilter,
				meta: {
					filterVariant: 'text',
				},
			}),
			columnHelper.accessor('city', {
				header: t('trainings.location'),
				filterFn: textFilter,
				meta: {
					filterVariant: 'text',
				},
			}),
			columnHelper.accessor('freeCapacity', {
				header: t('trainings.capacity'),
				cell: (cellInfo) => {
					if (!cellInfo.row.original.freeCapacity) {
						return null;
					}
					const existingReservation =
						(cellInfo.row.original.trainingReservations &&
							findExistingReservation(
								cellInfo.row.original.trainingReservations,
								activeCompany.id,
							)) ||
						{};

					return getCalculatedFreeCapacity(cellInfo.row.original.freeCapacity, existingReservation);
				},
			}),
			columnHelper.display({
				id: 'actions',
				cell: (cellInfo) => (
					<ActionCell {...cellInfo} onEnrollClick={onEnrollClick} onReserveClick={onReserveClick} />
				),
				enableColumnFilter: false,
			}),
		] as Array<AccessorKeyColumnDef<Partial<CourseDataEntry>, unknown>>;
	}, []);

	return <Table data={courses} columns={columns} />;
};

export default CourseAgendaOverview;
